import { useContext } from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, Form, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject, isValidPrice } from '@domain-group/javascript-utils'

import useOrder, { useOrderForm } from '@hooks/useOrder'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'EditOrderTotalModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateOrder: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditOrderTotalModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateOrder } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { order: initOrder } = modalPayload

  const {
    callbacks: { updateOrder: updateFn },
    order,
    order: {
      total,
    },
    updating,
  } = useOrder(initOrder)

  const orderState = {
    ...order,
    total_override: order.total_override || order.total || 0,
  }

  const orderFormPayload = useOrderForm(orderState, {
    customRequiredFields: [
      {
        key: 'override_reason',
        // @ts-expect-error TS2353
        validation: (value?: string) => value?.trim() !== '',
      },
      {
        key: 'total_override',
        // @ts-expect-error TS2353
        validation: isValidPrice,
      },
    ],
    validateOn: 'change',
  })

  const {
    entityState,
    entityState: {
      override_reason,
      total_override,
    },
    errors,
    handlers,
    saveEnabled,
  } = orderFormPayload

  // To be used with handleCallbackAction
  const updateOrderPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Order Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Override" titleSecondLine="Total" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Order Total">
            <Box
              alignItems="center"
              flexDirection="column"
              justifyContent="center"
              padding="large"
              variant="whiteGrey"
            >
              <Text fontSize="large">
                {total ? formatMoney(total) : '$0.00'}
              </Text>

              <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
                Order #{order.id || '0'}
              </Text>
            </Box>
          </Form.Field>

          <Form.Field label="* New Total" marginTop="large">
            <input
              data-value-type="number"
              name="total_override"
              type="number"
              value={total_override}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Authorisation / Reason" errorMessage={errors.override_reason} marginTop="large">
            <textarea
              name="override_reason"
              style={{ height: 160, resize: 'vertical' }}
              // @ts-expect-error TS2322
              type="text"
              value={override_reason}
              {...handlers}
            />
          </Form.Field>

          <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarNoticeBox.Title>
              <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />

              <Text color="orange" style={{ display: 'inline-block' }}>&nbsp;Override Notice
              </Text>

            </SidebarNoticeBox.Title>
            <SidebarNoticeBox.Paragraph>
              Please note that overriding the order total will override the total sent to the provider of the order.
            </SidebarNoticeBox.Paragraph>

            <SidebarNoticeBox.Paragraph>
              If you have not been authorised to do so the final invoice may not reflect what is entered here.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateOrder(updateOrderPayload)}
          size="large"
        >
          Confirm Override
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditOrderTotalModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
